import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetProductsQuery } from "../../api/productApi";
import { useAppDispatch } from "../../../../../common/hooks/hooks";
import { closeMultiPanelOffcanvas } from "../../../../../common/features/multiPanelOffcanvas/multiPanelOffcanvasSlice";
import ProductPanelItem from "../common/ProductPanelItem/ProductPanelItem";
import ProductPanelItemSkeleton from "../common/ProductPanelItem/ProductPanelItemSkeleton";
import Skeleton from "react-loading-skeleton";
import PanelFeaturedProducts from "../../../featured/components/Panel/PanelFeaturedProducts";
import PanelFeaturedCategories from "../../../featured/components/Panel/PanelFeaturedCategories";
import PanelFeaturedTags from "../../../featured/components/Panel/PanelFeaturedTags";

const SearchPanel: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [triggerSearch, { data, isFetching, isError }] =
    useLazyGetProductsQuery();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the input when the panel becomes active
    const timeoutId = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm.trim().length > 2) {
      // Limit increased to 30 as requested
      triggerSearch({ search: debouncedSearchTerm, limit: 30 });
    }
  }, [debouncedSearchTerm, triggerSearch]);

  // Combine the ready flag into the loading state
  const isComponentLoading = !areTranslationsReady;
  const products = data?.products || [];
  const showResults = debouncedSearchTerm.trim().length > 2;

  return (
    <div className="search-panel">
      <div className="search-panel__form">
        {isComponentLoading ? (
          // Show a skeleton for the form while translations load
          <Skeleton height={44} />
        ) : (
          <input
            ref={inputRef}
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t(
              "searchOffcanvas.placeholder",
              "Search for products..."
            )}
            className="search-panel__input"
          />
        )}
        {isFetching && <div className="search-panel__loader"></div>}
      </div>

      <div className="search-panel__results">
        {isFetching ? (
          <ul className="search-panel__items">
            {Array.from({ length: 3 }).map((_, index) => (
              <ProductPanelItemSkeleton key={index} />
            ))}
          </ul>
        ) : !showResults ? (
          // State 1: Initial / Empty / Short Input State
          <>
            <p className="search-panel__no-results">
              {t(
                "searchOffcanvas.startTyping",
                "Start typing to search (at least 3 characters)."
              )}
            </p>

            <PanelFeaturedProducts />
            <PanelFeaturedCategories />
            <PanelFeaturedTags />
          </>
        ) : // State 2: Error
        isError ? (
          <p className="search-panel__no-results search-panel__no-results--error">
            {t("searchOffcanvas.error", "An error occurred while searching.")}
          </p>
        ) : products.length === 0 ? (
          // State 3: No Results Found
          <p className="search-panel__no-results">
            {t(
              "searchOffcanvas.noResults",
              "No products found for '{{term}}'.",
              {
                term: debouncedSearchTerm,
              }
            )}
          </p>
        ) : (
          // State 4: Success with Results
          <ul className="search-panel__items">
            {products.map((product) => (
              <ProductPanelItem
                key={product.id}
                product={product}
                onItemClick={() => dispatch(closeMultiPanelOffcanvas())}
                showStockStatus={true}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchPanel;
